package com.sddamico.pages

import androidx.compose.runtime.*
import com.sddamico.HeadingFontFamily
import com.sddamico.SitePalette
import com.sddamico.api.ApiException
import com.sddamico.api.apiGet
import com.sddamico.components.layouts.PageLayoutData
import com.sddamico.components.widgets.EmptyState
import com.sddamico.components.widgets.ErrorCard
import com.sddamico.components.widgets.PageLoading
import com.sddamico.components.widgets.ScheduleCard
import com.sddamico.models.LeagueYearDetailResponse
import com.sddamico.models.TournamentRefResponse
import com.sddamico.util.daysBetween
import com.sddamico.util.monthName
import com.sddamico.components.motion.Durations
import com.sddamico.components.motion.Ease
import com.sddamico.components.motion.FadeInKeyframes
import com.sddamico.components.motion.SlidingUnderline
import com.sddamico.components.motion.stableBoldLabel
import com.sddamico.components.motion.underlineTarget
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

private const val PX_PER_DAY = 14.4
private val RULER_WIDTH = 80.px
private val MOBILE_RULER_WIDTH = 68.px
private val TODAY_COLOR = Color.rgb(0x4A8A7A)

private enum class ScheduleTab(val label: String, val mobileLabel: String, val sportKey: String) {
    GOLF("Golf", "Golf", "MENS_GOLF"),
    MENS_TENNIS("Men's Tennis", "M Tennis", "MENS_TENNIS"),
    WOMENS_TENNIS("Women's Tennis", "W Tennis", "WOMENS_TENNIS"),
}

/** Extract the league year from tournaments */
private fun leagueYear(tournaments: List<TournamentRefResponse>): Int {
    return tournaments.first().startDate.split("-")[0].toInt()
}

/** Today's date as ISO string */
private fun todayIso(): String {
    val d = kotlin.js.Date()
    val y = d.getFullYear()
    val m = (d.getMonth() + 1).toInt().toString().padStart(2, '0')
    val day = d.getDate().toInt().toString().padStart(2, '0')
    return "$y-$m-$day"
}

/** Get months between start and end dates for ruler labels */
private fun monthsBetween(startIso: String, endIso: String): List<Pair<String, Int>> {
    val startParts = startIso.split("-")
    val endParts = endIso.split("-")
    val startYear = startParts[0].toInt()
    val startMonth = startParts[1].toInt()
    val endYear = endParts[0].toInt()
    val endMonth = endParts[1].toInt()

    val months = mutableListOf<Pair<String, Int>>()
    var y = startYear
    var m = startMonth
    while (y < endYear || (y == endYear && m <= endMonth)) {
        val iso = "$y-${m.toString().padStart(2, '0')}-01"
        val offset = daysBetween(startIso, iso)
        months.add(monthName(m - 1) to offset)
        m++
        if (m > 12) { m = 1; y++ }
    }
    return months
}

/** Get Sunday positions for tick marks */
private fun sundayOffsets(startIso: String, totalDays: Int): List<Int> {
    val startDate = kotlin.js.Date(startIso)
    val offsets = mutableListOf<Int>()
    // Find first Sunday on or after start
    val startDow = startDate.getDay().toInt() // 0=Sun
    val firstSundayOffset = if (startDow == 0) 0 else 7 - startDow
    var offset = firstSundayOffset
    while (offset < totalDays) {
        offsets.add(offset)
        offset += 7
    }
    return offsets
}

@InitRoute
fun initSchedule(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Schedule", wideContent = true))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun SchedulePage() {
    var leagueData by remember { mutableStateOf<LeagueYearDetailResponse?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        try {
            leagueData = apiGet("/api/leagueyears/detail", LeagueYearDetailResponse.serializer())
        } catch (e: ApiException) {
            error = e.errorMessage
        } catch (e: Exception) {
            error = "Failed to load schedule"
        }
        loading = false
    }

    if (loading) {
        PageLoading()
        return
    }

    if (error != null) {
        ErrorCard(error!!)
        return
    }

    val data = leagueData
    if (data == null || data.tournaments.isEmpty()) {
        EmptyState("No tournaments scheduled")
        return
    }

    val scoredTournaments = data.tournaments.filter { it.isScored }

    Column(Modifier.fillMaxWidth().gap(1.cssRem)) {
        // Header
        SpanText(
            "Schedule",
            Modifier
                .fontFamily(*HeadingFontFamily)
                .fontSize(2.cssRem)
                .fontWeight(FontWeight.Bold)
                .color(SitePalette.text)
        )

        if (scoredTournaments.isEmpty()) {
            EmptyState("No scored tournaments scheduled")
        } else {
            // Desktop layout
            Div(Modifier.displayIfAtLeast(Breakpoint.MD).fillMaxWidth().toAttrs()) {
                DesktopTimeline(scoredTournaments)
            }

            // Mobile layout
            Div(Modifier.displayUntil(Breakpoint.MD).fillMaxWidth().toAttrs()) {
                MobileTimeline(scoredTournaments)
            }
        }
    }
}

@Composable
private fun DesktopTimeline(tournaments: List<TournamentRefResponse>) {
    val year = leagueYear(tournaments)
    val startIso = "$year-01-01"
    val endIso = "$year-12-31"
    val totalDays = daysBetween(startIso, endIso)
    val totalHeight = (totalDays * PX_PER_DAY).px
    val today = todayIso()
    val todayOffset = daysBetween(startIso, today)
    val months = monthsBetween(startIso, endIso)
    val sundays = sundayOffsets(startIso, totalDays)

    val golf = tournaments.filter { it.sport == "MENS_GOLF" }
    val mensTennis = tournaments.filter { it.sport == "MENS_TENNIS" }
    val womensTennis = tournaments.filter { it.sport == "WOMENS_TENNIS" }

    // Column headers
    Row(
        Modifier
            .fillMaxWidth()
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, SitePalette.border)
            .borderRadius(8.px)
            .borderBottom(0.px, LineStyle.None, Colors.Transparent)
            .styleModifier { property("border-bottom-left-radius", "0"); property("border-bottom-right-radius", "0") }
    ) {
        // Ruler header spacer
        Div(Modifier.width(RULER_WIDTH).flexShrink(0).toAttrs())

        val headers = listOf("Golf", "Men's Tennis", "Women's Tennis")
        headers.forEachIndexed { index, header ->
            if (index > 0) {
                Div(
                    Modifier
                        .width(1.px)
                        .backgroundColor(SitePalette.border)
                        .styleModifier { property("align-self", "stretch") }
                        .toAttrs()
                )
            }
            Div(
                Modifier
                    .flexGrow(1)
                    .flexBasis(0.px)
                    .padding(topBottom = 0.75.cssRem)
                    .textAlign(TextAlign.Center)
                    .fontSize(0.8125.cssRem)
                    .fontWeight(FontWeight.SemiBold)
                    .color(SitePalette.textSecondary)
                    .toAttrs()
            ) {
                Text(header)
            }
        }
    }

    // Timeline content — full height, page scrolls
    Div(
        Modifier
            .fillMaxWidth()
            .minHeight(totalHeight)
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, SitePalette.border)
            .styleModifier { property("border-top", "0"); property("border-bottom-left-radius", "8px"); property("border-bottom-right-radius", "8px") }
            .toAttrs()
    ) {
        Row(Modifier.fillMaxWidth().height(totalHeight).position(Position.Relative)) {
            // Left ruler
            TimelineRuler(months, sundays, todayOffset, totalHeight, RULER_WIDTH)

            // Three columns
            Div(
                Modifier
                    .flexGrow(1)
                    .height(totalHeight)
                    .position(Position.Relative)
                    .styleModifier { property("display", "flex") }
                    .toAttrs()
            ) {
                // Today line across all columns
                if (todayOffset in 0..totalDays) {
                    Div(
                        Modifier
                            .position(Position.Absolute)
                            .top((todayOffset * PX_PER_DAY).px)
                            .fillMaxWidth()
                            .height(1.px)
                            .styleModifier {
                                property("border-top", "1px dashed ${TODAY_COLOR}")
                                property("z-index", "5")
                            }
                            .toAttrs()
                    )
                }

                val columnData = listOf(golf, mensTennis, womensTennis)
                columnData.forEachIndexed { index, sportTournaments ->
                    if (index > 0) {
                        Div(
                            Modifier
                                .width(1.px)
                                .fillMaxHeight()
                                .backgroundColor(SitePalette.border)
                                .flexShrink(0)
                                .toAttrs()
                        )
                    }
                    SportColumn(sportTournaments, startIso, totalHeight)
                }
            }
        }

        // Today marker for auto-scroll
        if (todayOffset in 0..totalDays) {
            Div(
                Modifier
                    .position(Position.Relative)
                    .styleModifier { property("margin-top", "-${(totalDays - todayOffset) * PX_PER_DAY}px") }
                    .toAttrs { id("schedule-today") }
            )
        }
    }

    // Auto-scroll to today on page load
    LaunchedEffect(Unit) {
        kotlinx.browser.document.getElementById("schedule-today")?.let { el ->
            el.asDynamic().scrollIntoView(js("{behavior: 'smooth', block: 'center'}"))
        }
    }
}

@Composable
private fun MobileTimeline(tournaments: List<TournamentRefResponse>) {
    var activeTab by remember { mutableStateOf(ScheduleTab.GOLF) }

    val year = leagueYear(tournaments)
    val startIso = "$year-01-01"
    val endIso = "$year-12-31"
    val totalDays = daysBetween(startIso, endIso)
    val totalHeight = (totalDays * PX_PER_DAY).px
    val today = todayIso()
    val todayOffset = daysBetween(startIso, today)
    val months = monthsBetween(startIso, endIso)
    val sundays = sundayOffsets(startIso, totalDays)

    val filteredTournaments = tournaments.filter { it.sport == activeTab.sportKey }

    Column(Modifier.fillMaxWidth()) {
        // Tab bar
        SlidingUnderline(
            activeKey = activeTab,
            color = SitePalette.primary,
            gap = (-2).px,
            modifier = Modifier.fillMaxWidth().overflow(Overflow.Auto),
        ) {
            Row(
                Modifier
                    .fillMaxWidth()
                    .borderBottom(1.px, LineStyle.Solid, SitePalette.border)
                    .gap(0.px)
            ) {
                ScheduleTab.entries.forEach { tab ->
                    val isActive = tab == activeTab
                    Div(
                        Modifier
                            .padding(topBottom = 0.75.cssRem, leftRight = 1.cssRem)
                            .color(if (isActive) SitePalette.primary else SitePalette.textMuted)
                            .fontSize(0.875.cssRem)
                            .fontWeight(if (isActive) FontWeight.SemiBold else FontWeight.Medium)
                            .cursor(Cursor.Pointer)
                            .transition(Transition.of("color", Durations.HOVER.ms, Ease.Out.fn))
                            .onClick { activeTab = tab }
                            .styleModifier { property("white-space", "nowrap") }
                            .underlineTarget(isActive)
                            .toAttrs()
                    ) {
                        SpanText(tab.mobileLabel, Modifier.stableBoldLabel(tab.mobileLabel))
                    }
                }
            }
        }

        // Timeline content — full height, page scrolls
        Div(
            Modifier
                .fillMaxWidth()
                .minHeight(totalHeight)
                .backgroundColor(SitePalette.surface)
                .border(1.px, LineStyle.Solid, SitePalette.border)
                .borderRadius(8.px)
                .margin(top = 1.cssRem)
                .toAttrs()
        ) {
            Row(Modifier.fillMaxWidth().height(totalHeight).position(Position.Relative)) {
                TimelineRuler(months, sundays, todayOffset, totalHeight, MOBILE_RULER_WIDTH)

                Div(
                    Modifier
                        .flexGrow(1)
                        .height(totalHeight)
                        .position(Position.Relative)
                        .toAttrs()
                ) {
                    // Today line
                    if (todayOffset in 0..totalDays) {
                        Div(
                            Modifier
                                .position(Position.Absolute)
                                .top((todayOffset * PX_PER_DAY).px)
                                .fillMaxWidth()
                                .height(1.px)
                                .styleModifier {
                                    property("border-top", "1px dashed ${TODAY_COLOR}")
                                    property("z-index", "5")
                                }
                                .toAttrs()
                        )
                    }

                    // Tournament cards; the sport's cards fade in together when the tab changes. The fade sits on each
                    // card rather than a wrapper: an animating wrapper would flatten the cards' z-index ordering
                    // against the today line until the fade ends.
                    key(activeTab) {
                        filteredTournaments.forEach { tournament ->
                            val yOffset = daysBetween(startIso, tournament.startDate)
                            val durationPx = tournament.endDate?.let {
                                ((daysBetween(tournament.startDate, it) + 1) * PX_PER_DAY).px
                            }
                            Div(
                                Modifier
                                    .position(Position.Absolute)
                                    .top((yOffset * PX_PER_DAY).px)
                                    .fillMaxWidth()
                                    .padding(leftRight = 0.5.cssRem, top = 0.25.cssRem)
                                    .run { if (durationPx != null) minHeight(durationPx) else this }
                                    .styleModifier { property("display", "flex"); property("flex-direction", "column"); property("z-index", "$yOffset") }
                                    .animation(FadeInKeyframes.toAnimation(Durations.ENTER.ms, Ease.Out.fn))
                                    .toAttrs()
                            ) {
                                ScheduleCard(tournament)
                            }
                        }
                    }
                }
            }

            // Today marker for auto-scroll
            if (todayOffset in 0..totalDays) {
                Div(
                    Modifier
                        .position(Position.Relative)
                        .styleModifier { property("margin-top", "-${(totalDays - todayOffset) * PX_PER_DAY}px") }
                        .toAttrs { id("schedule-today-mobile") }
                )
            }
        }

        // Auto-scroll to today
        LaunchedEffect(activeTab) {
            kotlinx.browser.document.getElementById("schedule-today-mobile")?.let { el ->
                el.asDynamic().scrollIntoView(js("{behavior: 'smooth', block: 'center'}"))
            }
        }
    }
}

@Composable
private fun TimelineRuler(
    months: List<Pair<String, Int>>,
    sundays: List<Int>,
    todayOffset: Int,
    totalHeight: CSSSizeValue<CSSUnit.px>,
    rulerWidth: CSSSizeValue<CSSUnit.px>,
) {
    Div(
        Modifier
            .width(rulerWidth)
            .height(totalHeight)
            .flexShrink(0)
            .position(Position.Relative)
            .borderRight(1.px, LineStyle.Solid, SitePalette.border)
            .toAttrs()
    ) {
        // Month labels
        months.forEach { (name, offset) ->
            Div(
                Modifier
                    .position(Position.Absolute)
                    .top((offset * PX_PER_DAY).px)
                    .fillMaxWidth()
                    .padding(left = 0.5.cssRem, top = 0.25.cssRem)
                    .fontSize(0.625.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(SitePalette.textSecondary)
                    .styleModifier { property("letter-spacing", "0.08em") }
                    .toAttrs()
            ) {
                Text(name)
            }
            // Month divider line
            Div(
                Modifier
                    .position(Position.Absolute)
                    .top((offset * PX_PER_DAY).px)
                    .fillMaxWidth()
                    .height(1.px)
                    .backgroundColor(SitePalette.border)
                    .toAttrs()
            )
        }

        // Sunday tick marks
        sundays.forEach { offset ->
            Div(
                Modifier
                    .position(Position.Absolute)
                    .top((offset * PX_PER_DAY).px)
                    .width(8.px)
                    .height(1.px)
                    .backgroundColor(SitePalette.border)
                    .styleModifier { property("right", "0") }
                    .toAttrs()
            )
        }

        // Today badge
        if (todayOffset >= 0) {
            Div(
                Modifier
                    .position(Position.Absolute)
                    .top(((todayOffset * PX_PER_DAY) - 8).px)
                    .padding(topBottom = 0.0625.cssRem, leftRight = 0.25.cssRem)
                    .backgroundColor(TODAY_COLOR)
                    .borderRadius(3.px)
                    .fontSize(0.5.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(Colors.White)
                    .styleModifier { property("right", "0"); property("letter-spacing", "0.05em") }
                    .toAttrs()
            ) {
                Text("TODAY")
            }
        }
    }
}

@Composable
private fun SportColumn(
    tournaments: List<TournamentRefResponse>,
    startIso: String,
    totalHeight: CSSSizeValue<CSSUnit.px>,
) {
    Div(
        Modifier
            .flexGrow(1)
            .flexBasis(0.px)
            .height(totalHeight)
            .position(Position.Relative)
            .toAttrs()
    ) {
        tournaments.forEach { tournament ->
            val yOffset = daysBetween(startIso, tournament.startDate)
            val durationPx = tournament.endDate?.let {
                ((daysBetween(tournament.startDate, it) + 1) * PX_PER_DAY).px
            }
            Div(
                Modifier
                    .position(Position.Absolute)
                    .top((yOffset * PX_PER_DAY).px)
                    .fillMaxWidth()
                    .padding(leftRight = 0.375.cssRem, top = 0.25.cssRem)
                    .run { if (durationPx != null) minHeight(durationPx) else this }
                    .styleModifier { property("display", "flex"); property("flex-direction", "column"); property("z-index", "$yOffset") }
                    .toAttrs()
            ) {
                ScheduleCard(tournament)
            }
        }
    }
}
